'use client';

import React, { useState, useEffect } from 'react';

import Image from 'next/image';
import PlanInterestModal from './components/PlanInterestModal';
import { createClient } from '@/lib/supabase/client';

interface Plan {
  id: string;
  label: string;
  description: string;
  plan_type: string;
  price_brl: number;
  price_per_doc_brl: number;
  timestamp_price_brl: number;
  docs_included: number;
  is_active: boolean;
  sort_order: number;
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [planModal, setPlanModal] = useState<{plan: 'essential' | 'professional';label: string;} | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (data) setPlans(data);
    };
    fetchPlans();
  }, []);

  const formatBRL = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const avulso = plans.find(p => p.id === 'avulso');
  const mensal10 = plans.find(p => p.id === 'mensal_10');
  const mensal30 = plans.find(p => p.id === 'mensal_30');
  const mensal50 = plans.find(p => p.id === 'mensal_50');

  return (
    <div className="min-h-screen bg-[#050d1a] text-white font-sans overflow-x-hidden">
      {/* Plan Interest Modal */}
      {planModal &&
      <PlanInterestModal
        plan={planModal.plan}
        planLabel={planModal.label}
        onClose={() => setPlanModal(null)} />

      }
      {/* NAV */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#050d1a]/95 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`
        }>

        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image
              src="/assets/images/rd2_icon-1777353885177.png"
              alt="RD2Sign logo ícone"
              width={36}
              height={36}
              className="rounded-lg" />

            <span className="text-xl font-bold tracking-tight text-white">RD2Sign</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#solucao" className="hover:text-white transition-colors">Solução</a>
            <a href="#modalidades" className="hover:text-white transition-colors">Modalidades</a>
            <a href="#pacotes" className="hover:text-white transition-colors">Pacotes</a>
            <a href="#aplicacoes" className="hover:text-white transition-colors">Aplicações</a>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="/onboarding/cadastro"
              className="px-4 py-2 text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl transition-all">

              Assinar Plano
            </a>
            <a
              href="https://www.rd2sign.com/sign-up-login-screen"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold text-white bg-[#1a56db] hover:bg-[#1e40af] rounded-xl transition-all shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50">

              Login
            </a>
          </div>
        </div>
      </header>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#071020]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[#1a56db]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#0d9488]/8 rounded-full blur-[100px] pointer-events-none" />
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />


        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full text-xs font-semibold text-[#60a5fa] mb-6">
              <span className="w-1.5 h-1.5 bg-[#60a5fa] rounded-full animate-pulse" />
              Plataforma de Assinatura Digital
            </div>
            <h1 className="text-4xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6">
              Plataforma inteligente para{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#60a5fa] to-[#34d399]">
                assinatura digital
              </span>{' '}
              de documentos
            </h1>
            <p className="text-lg text-white/60 leading-relaxed mb-8 max-w-xl">
              Assine documentos com segurança, flexibilidade e controle, utilizando fluxos sequenciais ou simultâneos, certificados digitais ICP-Brasil, certificados de cadeia privada RD2 PCA ou assinatura simples realizada diretamente em tela.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.rd2sign.com/sign-up-login-screen"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#1a56db] hover:bg-[#1e40af] text-white font-semibold rounded-xl transition-all shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50">

                Acesso à Plataforma
              </a>
              <a
                href="mailto:info@rd2sign.com"
                className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl border border-white/20 transition-all">

                Solicitar Demonstração
              </a>
              <a
                href="mailto:comercial@rd2sign.com"
                className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white/80 font-semibold rounded-xl border border-white/10 transition-all">

                Falar com Especialista
              </a>
            </div>
          </div>

          {/* Hero visual — bento mini */}
          <div className="hidden lg:grid grid-cols-2 gap-4">
            {[
            { icon: '🔐', title: 'ICP-Brasil', desc: 'Certificados reconhecidos juridicamente', color: 'from-[#1a56db]/20 to-[#1a56db]/5' },
            { icon: '🏢', title: 'RD2 PCA', desc: 'Cadeia privada corporativa', color: 'from-[#0d9488]/20 to-[#0d9488]/5' },
            { icon: '✍️', title: 'Assinatura em Tela', desc: 'Simples, rápida e rastreável', color: 'from-[#7c3aed]/20 to-[#7c3aed]/5' },
            { icon: '📋', title: 'Trilha de Auditoria', desc: 'Histórico completo do processo', color: 'from-[#d97706]/20 to-[#d97706]/5' }]?.
            map((item) =>
            <div
              key={item?.title}
              className={`p-5 rounded-2xl bg-gradient-to-br ${item?.color} border border-white/10 backdrop-blur-sm`}>

                <div className="text-3xl mb-3">{item?.icon}</div>
                <div className="font-bold text-white text-sm mb-1">{item?.title}</div>
                <div className="text-white/50 text-xs leading-relaxed">{item?.desc}</div>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30 text-xs">
          <span>Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
        </div>
      </section>
      {/* ABOUT */}
      <section id="solucao" className="py-24 bg-[#070f1e]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-4">O que é o RD2Sign?</p>
              <h2 className="text-3xl lg:text-4xl font-extrabold leading-tight mb-6">
                Assinaturas digitais para empresas que precisam de{' '}
                <span className="text-[#34d399]">segurança, agilidade e rastreabilidade</span>
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                O RD2Sign é uma plataforma de assinatura eletrônica e digital para envio, controle e conclusão de documentos que precisam ser assinados por uma ou várias partes.
              </p>
              <p className="text-white/60 leading-relaxed">
                Processos de assinatura ainda podem ser lentos, fragmentados e vulneráveis a falhas operacionais. Contratos, termos, autorizações, laudos, documentos internos e processos regulatórios exigem mais do que apenas uma assinatura: exigem controle, evidência, governança e rastreabilidade.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
              'Envio de documentos para assinatura',
              'Assinaturas sequenciais ou simultâneas',
              'Múltiplos signatários',
              'Certificados digitais ICP-Brasil',
              'Certificados RD2 PCA (cadeia privada)',
              'Assinatura simples em tela',
              'Trilha de auditoria completa',
              'Controle de status dos documentos',
              'Integração com sistemas corporativos',
              'Segurança, privacidade e governança']?.
              map((item) =>
              <div key={item} className="flex items-start gap-2 p-3 rounded-xl bg-white/5 border border-white/8">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  <span className="text-white/70 text-sm leading-snug">{item}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* MODALIDADES */}
      <section id="modalidades" className="py-24 bg-[#050d1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Modalidades</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">Três modalidades de assinatura em uma única plataforma</h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Card 1 */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1520] border border-white/10 overflow-hidden group hover:border-[#7c3aed]/50 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#7c3aed]/10 rounded-full blur-2xl group-hover:bg-[#7c3aed]/20 transition-all" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#7c3aed]/20 flex items-center justify-center text-2xl mb-5">✍️</div>
                <div className="text-xs font-bold text-[#a78bfa] uppercase tracking-widest mb-2">Modalidade 1</div>
                <h3 className="text-xl font-bold mb-3">Assinatura Simples em Tela</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-4">
                  O usuário assina diretamente na tela, com registro das evidências do processo — data, hora, identificação do assinante e histórico da operação.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/40 font-semibold uppercase tracking-wider mb-2">Ideal para</p>
                  <p className="text-white/55 text-sm">Termos internos, confirmações, autorizações simples, aceite de políticas, recibos e documentos operacionais.</p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1520] border border-white/10 overflow-hidden group hover:border-[#0d9488]/50 transition-all lg:scale-105 lg:shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#0d9488]/10 rounded-full blur-2xl group-hover:bg-[#0d9488]/20 transition-all" />
              <div className="absolute top-4 right-4 px-2 py-0.5 bg-[#0d9488]/20 border border-[#0d9488]/40 rounded-full text-[10px] font-bold text-[#34d399] uppercase tracking-wider">Corporativo</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#0d9488]/20 flex items-center justify-center text-2xl mb-5">🏢</div>
                <div className="text-xs font-bold text-[#34d399] uppercase tracking-widest mb-2">Modalidade 2</div>
                <h3 className="text-xl font-bold mb-3">Certificado Digital RD2 PCA</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-4">
                  Cadeia privada de certificados digitais para controle corporativo. Adequada para ecossistemas fechados, redes privadas, ambientes industriais e processos internos críticos.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/40 font-semibold uppercase tracking-wider mb-2">Ideal para</p>
                  <p className="text-white/55 text-sm">Ambientes corporativos privados, redes de fornecedores, autenticação de operadores, registros técnicos e fluxos de governança.</p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1520] border border-white/10 overflow-hidden group hover:border-[#1a56db]/50 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#1a56db]/10 rounded-full blur-2xl group-hover:bg-[#1a56db]/20 transition-all" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#1a56db]/20 flex items-center justify-center text-2xl mb-5">🔐</div>
                <div className="text-xs font-bold text-[#60a5fa] uppercase tracking-widest mb-2">Modalidade 3</div>
                <h3 className="text-xl font-bold mb-3">Certificado Digital ICP-Brasil</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-4">
                  Para documentos que exigem maior robustez jurídica. Suporte a certificados A1 ou A3, respeitando o enquadramento jurídico aplicável ao tipo de documento.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/40 font-semibold uppercase tracking-wider mb-2">Ideal para</p>
                  <p className="text-white/55 text-sm">Contratos formais, documentos regulatórios, autorizações de alto impacto e processos com exigência jurídica elevada.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 p-5 rounded-xl bg-[#d97706]/10 border border-[#d97706]/30 text-sm text-[#fbbf24]/80 leading-relaxed">
            <span className="font-semibold text-[#fbbf24]">⚠️ Observação: </span>
            Quando o documento exigir enquadramento jurídico específico, a empresa deve avaliar a modalidade adequada de assinatura — simples, avançada ou qualificada — de acordo com a legislação aplicável e a política de governança documental da organização.
          </div>
        </div>
      </section>
      {/* FLUXOS */}
      <section className="py-24 bg-[#070f1e]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Fluxos de Assinatura</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">Assinaturas sequenciais ou simultâneas</h2>
            <p className="text-white/50 mt-4 max-w-xl mx-auto">Com o RD2Sign, sua empresa define como o processo deve ocorrer.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1520] border border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#1a56db]/20 flex items-center justify-center text-xl">⬇️</div>
                <h3 className="text-xl font-bold">Assinatura Sequencial</h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                O documento segue uma ordem pré-definida de aprovação e assinatura. Ideal para processos com hierarquia, aprovação interna ou validação em etapas.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {['Analista', 'Gerente', 'Diretor', 'Cliente']?.map((role, i, arr) =>
                <React.Fragment key={role}>
                    <span className="px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/30 rounded-lg text-sm font-medium text-[#93c5fd]">{role}</span>
                    {i < arr?.length - 1 && <span className="text-white/30 text-lg">→</span>}
                  </React.Fragment>
                )}
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1520] border border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#0d9488]/20 flex items-center justify-center text-xl">⚡</div>
                <h3 className="text-xl font-bold">Assinatura Simultânea</h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                Todos os signatários recebem o documento ao mesmo tempo e podem assinar independentemente da ordem. Ideal para contratos, termos de aceite e processos que precisam de velocidade.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {['Cliente', 'Fornecedor', 'Testemunha']?.map((role) =>
                <span key={role} className="px-3 py-1.5 bg-[#0d9488]/20 border border-[#0d9488]/30 rounded-lg text-sm font-medium text-[#6ee7b7]">{role}</span>
                )}
                <span className="text-white/40 text-xs ml-1">recebem simultaneamente</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* POR QUE ESCOLHER */}
      <section className="py-24 bg-[#050d1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Diferenciais</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">Por que escolher o RD2Sign?</h2>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="col-span-2 lg:col-span-1 row-span-2 p-8 rounded-2xl bg-gradient-to-br from-[#1a56db]/20 to-[#1a56db]/5 border border-[#1a56db]/30 flex flex-col justify-between">
              <div>
                <div className="text-4xl mb-4">🔒</div>
                <h3 className="text-2xl font-bold mb-3">Segurança</h3>
                <p className="text-white/55 text-sm leading-relaxed">
                  A plataforma permite o uso de diferentes níveis de assinatura, desde a assinatura simples até certificados digitais ICP-Brasil e certificados privados RD2 PCA.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/30 font-mono">SECURITY_LEVEL: HIGH</div>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#0d9488]/15 to-[#0d9488]/5 border border-[#0d9488]/25">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="text-lg font-bold mb-2">Governança</h3>
              <p className="text-white/55 text-sm leading-relaxed">Cada etapa registrada, organizada e acompanhada, reduzindo falhas manuais.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#7c3aed]/15 to-[#7c3aed]/5 border border-[#7c3aed]/25">
              <div className="text-3xl mb-3">🔄</div>
              <h3 className="text-lg font-bold mb-2">Flexibilidade</h3>
              <p className="text-white/55 text-sm leading-relaxed">Escolha o modelo de assinatura mais adequado para cada tipo de documento.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#d97706]/15 to-[#d97706]/5 border border-[#d97706]/25">
              <div className="text-3xl mb-3">🔍</div>
              <h3 className="text-lg font-bold mb-2">Rastreabilidade</h3>
              <p className="text-white/55 text-sm leading-relaxed">Histórico completo do fluxo de assinatura para maior transparência e auditoria.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#ec4899]/15 to-[#ec4899]/5 border border-[#ec4899]/25">
              <div className="text-3xl mb-3">🔗</div>
              <h3 className="text-lg font-bold mb-2">Integração</h3>
              <p className="text-white/55 text-sm leading-relaxed">Integração com ERPs, CRMs, portais corporativos e plataformas internas.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#0891b2]/15 to-[#0891b2]/5 border border-[#0891b2]/25">
              <div className="text-3xl mb-3">🛡️</div>
              <h3 className="text-lg font-bold mb-2">Privacidade</h3>
              <p className="text-white/55 text-sm leading-relaxed">Estruturada de acordo com as necessidades de privacidade e controle da organização.</p>
            </div>
          </div>
        </div>
      </section>
      {/* FUNCIONALIDADES */}
      <section className="py-24 bg-[#070f1e]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Funcionalidades</p>
              <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Principais funcionalidades da plataforma</h2>
              <p className="text-white/55 leading-relaxed">
                Uma solução completa para digitalizar e automatizar todos os seus processos de assinatura documental.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
              'Upload e envio de documentos',
              'Cadastro de signatários',
              'Assinaturas sequenciais',
              'Assinaturas simultâneas',
              'Notificações aos signatários',
              'Assinatura em tela',
              'Certificado ICP-Brasil',
              'Certificado RD2 PCA',
              'Painel de acompanhamento',
              'Registro de evidências',
              'Trilha de auditoria',
              'Gestão de documentos',
              'Integração via API',
              'Perfis e controle de acesso',
              'Relatórios gerenciais',
              'Customização de fluxos']?.
              map((f) =>
              <div key={f} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/8 text-sm text-white/65">
                  <span className="text-[#34d399] text-xs">●</span>
                  {f}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* APLICAÇÕES */}
      <section id="aplicacoes" className="py-24 bg-[#050d1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Aplicações</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">Aplicações do RD2Sign</h2>
            <p className="text-white/50 mt-4 max-w-xl mx-auto">O RD2Sign pode ser utilizado em diferentes áreas e segmentos</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
            { icon: '⚖️', area: 'Jurídico', desc: 'Contratos, procurações, aditivos, termos de responsabilidade e documentos societários.' },
            { icon: '👥', area: 'Recursos Humanos', desc: 'Contratos de trabalho, termos de ciência, políticas internas e documentos admissionais.' },
            { icon: '🏥', area: 'Saúde', desc: 'Termos de consentimento, laudos, autorizações e documentos clínicos.' },
            { icon: '🏛️', area: 'Governo', desc: 'Processos administrativos, declarações, autorizações e documentos institucionais.' },
            { icon: '🎓', area: 'Educação', desc: 'Matrículas, termos de aceite, declarações e contratos educacionais.' },
            { icon: '🏭', area: 'Indústria', desc: 'Ordens de serviço, registros técnicos, aprovações de produção e controles de qualidade.' },
            { icon: '💰', area: 'Financeiro', desc: 'Contratos, propostas, autorizações, termos de aceite e documentos de compliance.' },
            { icon: '🌐', area: 'Outros Segmentos', desc: 'Qualquer organização que precise digitalizar e rastrear processos de assinatura.' }]?.
            map((item) =>
            <div key={item?.area} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group">
                <div className="text-2xl mb-3">{item?.icon}</div>
                <h3 className="font-bold text-sm mb-2 text-white group-hover:text-[#60a5fa] transition-colors">{item?.area}</h3>
                <p className="text-white/45 text-xs leading-relaxed">{item?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* PACOTES */}
      <section id="pacotes" className="py-24 bg-[#070f1e]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Pacotes</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">Escolha o pacote ideal para sua organização</h2>
            <p className="text-white/50 mt-4 max-w-2xl mx-auto">Sem limite de assinadores por documento. Carimbo de tempo disponível em todos os pacotes.</p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6 mb-10">
            {/* Avulso */}
            <div className="p-7 rounded-2xl bg-[#0f1520] border border-white/10 flex flex-col hover:border-white/25 transition-all">
              <div className="mb-5">
                <div className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2">Pacote 1</div>
                <h3 className="text-xl font-extrabold mb-1">Assinatura Avulsa</h3>
                <p className="text-white/50 text-xs leading-relaxed">Por documento, sem mensalidade. Pague apenas quando usar.</p>
              </div>
              <div className="mb-5 pb-5 border-b border-white/10">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-extrabold text-[#34d399]">R$ {avulso ? formatBRL(avulso.price_brl) : '—'}</span>
                  <span className="text-white/40 text-sm">/documento</span>
                </div>
                <p className="text-white/40 text-xs">= R$ {avulso ? formatBRL(avulso.price_per_doc_brl) : '—'} por doc (sem limite de assinadores)</p>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Sem limite de assinadores
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Sem mensalidade
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Carimbo de tempo: R$ 0,01/assinatura
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Trilha de auditoria completa
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <a href="/onboarding/cadastro?plan=avulso" className="block w-full text-center px-4 py-3 rounded-xl border border-white/20 text-white/80 font-semibold text-sm hover:bg-white/10 transition-all">
                  Começar agora
                </a>
              </div>
            </div>

            {/* Mensal 10 */}
            <div className="p-7 rounded-2xl bg-[#0f1520] border border-white/10 flex flex-col hover:border-white/25 transition-all">
              <div className="mb-5">
                <div className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2">Pacote 2</div>
                <h3 className="text-xl font-extrabold mb-1">Mensal 10 Docs</h3>
                <p className="text-white/50 text-xs leading-relaxed">Até 10 documentos por mês. Documento excedente cobrado à parte.</p>
              </div>
              <div className="mb-5 pb-5 border-b border-white/10">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-extrabold text-[#60a5fa]">R$ {mensal10 ? formatBRL(mensal10.price_brl) : '—'}</span>
                  <span className="text-white/40 text-sm">/mês</span>
                </div>
                <p className="text-white/40 text-xs">= R$ {mensal10 ? formatBRL(mensal10.price_per_doc_brl) : '—'} por doc (sem limite de assinadores)</p>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  10 documentos inclusos/mês
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Sem limite de assinadores
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Carimbo de tempo: R$ 0,01/assinatura
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Documento excedente cobrado à parte
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <a href="/onboarding/cadastro?plan=mensal_10" className="block w-full text-center px-4 py-3 rounded-xl border border-white/20 text-white/80 font-semibold text-sm hover:bg-white/10 transition-all">
                  Começar agora
                </a>
              </div>
            </div>

            {/* Mensal 30 — Recomendado */}
            <div className="relative p-7 rounded-2xl bg-gradient-to-br from-[#1a56db]/20 to-[#0f1520] border border-[#1a56db]/40 flex flex-col shadow-2xl shadow-[#1a56db]/10 lg:scale-105">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#1a56db] rounded-full text-xs font-bold text-white uppercase tracking-wider">Mais Popular</div>
              <div className="mb-5">
                <div className="text-xs font-bold text-[#60a5fa] uppercase tracking-widest mb-2">Pacote 3</div>
                <h3 className="text-xl font-extrabold mb-1">Mensal 30 Docs</h3>
                <p className="text-white/50 text-xs leading-relaxed">Até 30 documentos por mês. Documento excedente cobrado à parte.</p>
              </div>
              <div className="mb-5 pb-5 border-b border-white/10">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-extrabold text-[#60a5fa]">R$ {mensal30 ? formatBRL(mensal30.price_brl) : '—'}</span>
                  <span className="text-white/40 text-sm">/mês</span>
                </div>
                <p className="text-white/40 text-xs">= R$ {mensal30 ? formatBRL(mensal30.price_per_doc_brl) : '—'} por doc (sem limite de assinadores)</p>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  30 documentos inclusos/mês
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Sem limite de assinadores
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Carimbo de tempo: R$ 0,01/assinatura
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#60a5fa] mt-0.5 flex-shrink-0">✓</span>
                  Documento excedente cobrado à parte
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <a href="/onboarding/cadastro?plan=mensal_30" className="block w-full text-center px-4 py-3 rounded-xl bg-[#1a56db] text-white font-semibold text-sm hover:bg-[#1e40af] transition-all">
                  Começar agora
                </a>
              </div>
            </div>

            {/* Mensal 50 */}
            <div className="p-7 rounded-2xl bg-gradient-to-br from-[#0d9488]/15 to-[#0f1520] border border-[#0d9488]/30 flex flex-col hover:border-[#0d9488]/50 transition-all">
              <div className="mb-5">
                <div className="text-xs font-bold text-[#34d399] uppercase tracking-widest mb-2">Pacote 4</div>
                <h3 className="text-xl font-extrabold mb-1">Mensal 50 Docs</h3>
                <p className="text-white/50 text-xs leading-relaxed">Até 50 documentos por mês. Melhor custo por documento.</p>
              </div>
              <div className="mb-5 pb-5 border-b border-white/10">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-extrabold text-[#34d399]">R$ {mensal50 ? formatBRL(mensal50.price_brl) : '—'}</span>
                  <span className="text-white/40 text-sm">/mês</span>
                </div>
                <p className="text-white/40 text-xs">= R$ {mensal50 ? formatBRL(mensal50.price_per_doc_brl) : '—'} por doc (sem limite de assinadores)</p>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  50 documentos inclusos/mês
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Sem limite de assinadores
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Carimbo de tempo: R$ 0,01/assinatura
                </div>
                <div className="flex items-start gap-2 text-sm text-white/60">
                  <span className="text-[#34d399] mt-0.5 flex-shrink-0">✓</span>
                  Documento excedente cobrado à parte
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <a href="/onboarding/cadastro?plan=mensal_50" className="block w-full text-center px-4 py-3 rounded-xl border border-[#0d9488]/50 text-[#34d399] font-semibold text-sm hover:bg-[#0d9488]/20 transition-all">
                  Começar agora
                </a>
              </div>
            </div>
          </div>

          {/* Carimbo de tempo note */}
          <div className="p-5 rounded-xl bg-[#1a56db]/10 border border-[#1a56db]/30 text-sm text-white/60 leading-relaxed text-center">
            <span className="font-semibold text-[#60a5fa]">⏱ Carimbo de Tempo: </span>
            Disponível em todos os pacotes. Custo adicional de <strong className="text-white/80">R$ 0,01 por assinatura</strong> quando solicitado. Todos os valores são armazenados e gerenciados no banco de dados da plataforma.
          </div>
        </div>
      </section>
      {/* ECOSSISTEMA RD2BUZZ */}
      <section className="py-24 bg-[#050d1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#60a5fa] text-sm font-semibold uppercase tracking-widest mb-3">Ecossistema RD2Buzz</p>
              <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Diferenciais RD2Buzz</h2>
              <p className="text-white/55 leading-relaxed mb-6">
                O RD2Sign não é apenas uma ferramenta de assinatura. Ele faz parte do ecossistema tecnológico da RD2Buzz, que combina identidade digital, autenticidade, rastreabilidade, certificados privados e registros imutáveis.
              </p>
              <p className="text-white/55 leading-relaxed">
                A plataforma pode ser integrada a outros componentes da RD2Buzz para uma solução completa de confiança digital.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {[
              { name: 'RD2 PCA', desc: 'Geração de certificados digitais de cadeia privada', color: 'border-[#0d9488]/30 bg-[#0d9488]/10' },
              { name: 'RD2 CSC', desc: 'Autenticidade offline por QR Code ou NFC', color: 'border-[#1a56db]/30 bg-[#1a56db]/10' },
              { name: 'RD2 SCE', desc: 'Registros em Blockchain privada e contratos inteligentes', color: 'border-[#7c3aed]/30 bg-[#7c3aed]/10' },
              { name: 'RD2 iD', desc: 'Identificação e credenciais digitais', color: 'border-[#d97706]/30 bg-[#d97706]/10' },
              { name: 'RD2 Signer', desc: 'Motor de assinatura digital para integrações específicas', color: 'border-[#ec4899]/30 bg-[#ec4899]/10' }]?.
              map((item) =>
              <div key={item?.name} className={`flex items-center gap-4 p-4 rounded-xl border ${item?.color}`}>
                  <div className="font-mono font-bold text-sm text-white/80 w-28 flex-shrink-0">{item?.name}</div>
                  <div className="text-white/50 text-sm">{item?.desc}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* CTA FINAL */}
      <section className="py-24 bg-[#070f1e] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a56db]/10 via-transparent to-[#0d9488]/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#1a56db]/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold leading-tight mb-6">
            Transforme seus processos de assinatura em uma{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#60a5fa] to-[#34d399]">
              jornada digital segura
            </span>
          </h2>
          <p className="text-white/55 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Com o RD2Sign, sua empresa reduz burocracia, acelera processos, aumenta a governança documental e escolhe o nível de assinatura mais adequado para cada operação.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:suporte@rd2sign.com"
              className="px-8 py-4 bg-[#1a56db] hover:bg-[#1e40af] text-white font-bold rounded-xl transition-all shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50">

              Agendar Demonstração
            </a>
            <a
              href="#pacotes"
              className="px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-bold rounded-xl border border-white/20 transition-all">

              Conhecer os Pacotes
            </a>
            <a
              href="mailto:info@rd2sign.com"
              className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white/80 font-bold rounded-xl border border-white/10 transition-all">

              Falar com a RD2Buzz
            </a>
          </div>
          <p className="mt-8 text-white/30 text-sm font-medium italic">
            RD2Sign — Assinaturas simples, avançadas e qualificadas em uma única plataforma.
          </p>
        </div>
      </section>
      {/* FOOTER */}
      <footer className="bg-[#050d1a] border-t border-white/8 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Image
              src="/assets/images/rd2_icon-1777353885177.png"
              alt="RD2Sign ícone"
              width={28}
              height={28}
              className="rounded-md opacity-70" />

            <span className="text-white/50 text-sm">RD2Sign — Uma solução <span className="text-white/70 font-semibold">RD2Buzz</span></span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <a href="mailto:suporte@rd2sign.com" className="hover:text-white/70 transition-colors">suporte@rd2sign.com</a>
            <a href="mailto:info@rd2sign.com" className="hover:text-white/70 transition-colors">info@rd2sign.com</a>
            <a href="https://www.rd2sign.com/sign-up-login-screen" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Login</a>
          </div>
        </div>
      </footer>
    </div>);

}